import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploader } from "@/components/ImageUploader";
import { VideoUploader } from "@/components/VideoUploader";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";
import { US_STATES } from "@/lib/listings";
import { SERVICE_CATEGORIES, SUB_LABELS, subLabel } from "@/lib/services";
import { Save, ArrowRight, Loader2, Briefcase, Trash2, Edit, Plus, ExternalLink, CreditCard } from "lucide-react";
import { toast } from "sonner";

const empty = {
  full_name: "", business_name: "", profession: "", category: "",
  description: "", profile_photo: "", cover_photo: "",
  phone: "", whatsapp: "", email: "", website: "", service_area: "",
  state: "", city: "", zip_code: "",
  years_experience: "", languages: ["Arabic", "English"],
  business_hours: "", portfolio_photos: [], portfolio_videos: [], certifications: []
};

const StatusBadge = ({ status, t }) => {
  const cls = status === "approved" ? "bg-green-500/20 text-green-400" :
    status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
    status === "rejected" ? "bg-red-500/20 text-red-400" : "bg-slate-700 text-slate-300";
  const label = status === "approved" ? t("منشور", "Live") :
    status === "pending" ? t("قيد المراجعة", "Pending") :
    status === "rejected" ? t("مرفوض", "Rejected") : t("مؤرشف", "Archived");
  return <Badge className={cls}>{label}</Badge>;
};

/**
 * Subscription card embedded inside each provider listing. Renders:
 *  - Current subscription status (Active / Inactive / Expired) with the expiry date.
 *  - A "listing hidden until renewal" notice when not active.
 *  - Activate / Subscribe / Renew buttons for the three tiers:
 *      Free first month (one-time) → $4.99 second month (one-time) → $15/mo from third month.
 * Visual style is in-line with the rest of the page — Royal Blue glass cards, no new tokens.
 */
const SubscriptionBlock = ({ provider, plans, busy, onSubscribe, t }) => {
  const isActive       = provider.subscription_status === "active";
  const freeUsed       = !!provider.free_trial_used;
  const introUsed      = !!provider.intro_offer_used;
  const expIso         = provider.subscription_expires_at;
  let   expLabel       = null;
  let   isExpired      = false;
  if (expIso) {
    try {
      const exp = new Date(expIso);
      isExpired = exp.getTime() < Date.now();
      expLabel  = exp.toLocaleDateString();
    } catch (_) { /* ignore */ }
  }
  // Derived label for the badge — gives "Expired" precedence over the raw status.
  let label, cls;
  if (isActive && !isExpired) {
    label = t("مفعّل", "Active");
    cls   = "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  } else if (isExpired) {
    label = t("منتهي", "Expired");
    cls   = "bg-red-500/20 text-red-300 border-red-500/30";
  } else {
    label = t("غير مفعّل", "Inactive");
    cls   = "bg-slate-600/30 text-slate-300 border-slate-500/40";
  }
  // The primary CTA label depends on whether the listing currently has an active
  // subscription. We show "Activate" when not active, otherwise "Renew".
  const activateLabel = (isActive && !isExpired) ? t("تجديد الاشتراك", "Renew subscription") : t("تفعيل الاشتراك", "Activate subscription");
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 space-y-2" data-testid={`sub-block-${provider.provider_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${cls}`} data-testid={`sub-status-${provider.provider_id}`}>{label}</span>
        {expLabel && (
          <span className="text-[11px] text-slate-400" data-testid={`sub-exp-${provider.provider_id}`}>
            {(isActive && !isExpired) ? t("ينتهي:", "Expires:") : t("انتهى:", "Ended:")} {expLabel}
          </span>
        )}
      </div>
      {!(isActive && !isExpired) && (
        <p className="text-[12px] text-slate-400 leading-snug">
          {t(
            "لن يظهر إعلانك في نتائج البحث للعملاء حتى يتم تفعيل الاشتراك.",
            "Your listing will not appear in customer search results until the subscription is activated."
          )}
        </p>
      )}
      <div className="text-[11px] text-slate-500 leading-snug pt-0.5">
        {t(
          "🎁 الشهر الأول مجاناً · 🔥 الشهر الثاني $4.99 · 💼 من الشهر الثالث $15/شهر",
          "🎁 First month free · 🔥 Second month $4.99 · 💼 From the third month $15/mo"
        )}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {!freeUsed && plans?.free && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onSubscribe("free")}
            className="btn-primary text-xs"
            data-testid={`sub-free-${provider.provider_id}`}
          >
            {busy ? t("...", "...") : t("🎁 تفعيل الشهر المجاني", "🎁 Activate Free Month")}
          </Button>
        )}
        {freeUsed && !introUsed && plans?.intro && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onSubscribe("intro")}
            className="btn-primary text-xs"
            data-testid={`sub-intro-${provider.provider_id}`}
          >
            {busy ? t("...", "...") : t(
              `🔥 الشهر الثاني — $${plans.intro.price}`,
              `🔥 Second Month — $${plans.intro.price}`
            )}
          </Button>
        )}
        {plans?.monthly && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onSubscribe("monthly")}
            variant="outline"
            className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 text-xs"
            data-testid={`sub-monthly-${provider.provider_id}`}
          >
            {busy ? t("...", "...") : (isActive && !isExpired)
              ? t(`${activateLabel} — $${plans.monthly.price}/شهر`, `${activateLabel} — $${plans.monthly.price}/mo`)
              : t(`💼 اشتراك شهري — $${plans.monthly.price}`, `💼 Monthly — $${plans.monthly.price}`)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default function MyProvider() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list, "new"=create, "<id>"=edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState(null);
  const [subBusy, setSubBusy] = useState(null); // provider_id currently subscribing, for spinner
  const [portalBusy, setPortalBusy] = useState(false);

  /**
   * Open the Stripe Customer Portal in the same tab. The backend looks up the
   * user's Stripe customer id (saved on any prior paid subscription) and creates
   * a fresh portal session so they can update payment methods, view invoices,
   * and cancel a subscription directly inside Stripe's hosted UI.
   */
  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await axios.post(
        `${API}/payments/billing-portal`,
        { return_url: window.location.origin + "/my-provider" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error(t("تعذّر فتح بوابة الفوترة.", "Could not open billing portal."));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message_ar || detail?.message_en || t("لا يوجد حساب فوترة بعد. أكمل أول اشتراك مدفوع.", "No billing account yet. Complete your first paid subscription."));
      toast.error(msg);
    } finally {
      setPortalBusy(false);
    }
  };

  const loadMine = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/providers/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setMine(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { loadMine(); }, []); // eslint-disable-line
  useEffect(() => {
    // Load the public plans metadata once — used by every listing card.
    axios.get(`${API}/providers/plans`).then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  /**
   * Subscribe / renew a provider listing under the chosen plan.
   *   - plan="free" → backend activates instantly, we just toast + reload.
   *   - plan="intro"|"monthly" → backend returns a Stripe Checkout URL; redirect
   *     the browser to Stripe so the card is charged. After payment, Stripe sends
   *     the user back to /payment/success which polls activation status.
   * The backend enforces "free/intro are one-time per listing" and extends the
   * expiry from the current end-date on renewals — we only send the plan choice.
   */
  const subscribe = async (providerId, plan) => {
    setSubBusy(providerId);
    try {
      const r = await axios.post(
        `${API}/providers/${providerId}/subscribe?plan=${plan}`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Paid plans → redirect to Stripe Checkout
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      // Free tier → instant activation feedback
      toast.success(r.data?.message_ar || t("تم تفعيل الاشتراك بنجاح.", "Subscription activated successfully."));
      await loadMine();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message_ar || detail?.message_en || t("تعذّر تفعيل الاشتراك. حاول مرة أخرى.", "Couldn't activate subscription. Please try again."));
      toast.error(msg);
    } finally {
      setSubBusy(null);
    }
  };

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (p) => {
    setForm({
      ...empty, ...p,
      years_experience: p.years_experience ?? "",
      languages: p.languages || [],
      portfolio_photos: p.portfolio_photos || [],
      portfolio_videos: p.portfolio_videos || [],
      certifications: p.certifications || []
    });
    setEditing(p.provider_id);
  };

  const remove = async (id) => {
    if (!window.confirm(t("تأكيد الحذف؟", "Confirm delete?"))) return;
    try {
      await axios.delete(`${API}/providers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      loadMine();
    } catch { toast.error(t("فشل", "Failed")); }
  };

  const save = async () => {
    const required = ["full_name", "profession", "category", "description", "phone", "state", "city", "zip_code"];
    for (const f of required) {
      if (!form[f]) { toast.error(t("املأ كل الحقول المطلوبة", "Fill required fields")); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        languages: form.languages.filter(Boolean)
      };
      if (editing === "new") {
        const r = await axios.post(`${API}/providers`, payload, { headers: { Authorization: `Bearer ${token}` } });
        const newId = r.data.provider_id;
        // Register portfolio photos with the moderation pipeline now that we have a provider_id.
        if (newId && (form.portfolio_photos || []).length > 0) {
          await Promise.all(form.portfolio_photos.map((path) =>
            axios.post(`${API}/media`, {
              listing_type: "provider", listing_id: newId, url: path, media_type: "image",
            }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
          ));
        }
        if (newId && (form.portfolio_videos || []).length > 0) {
          await Promise.all(form.portfolio_videos.map((path) =>
            axios.post(`${API}/media`, {
              listing_type: "provider", listing_id: newId, url: path, media_type: "video",
            }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
          ));
        }
        toast.success(t("تم إرسال ملفك للمراجعة", "Submitted for review"));
      } else {
        await axios.put(`${API}/providers/${editing}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تم الحفظ", "Saved"));
      }
      setEditing(null); loadMine();
    } catch (e) { toast.error(e.response?.data?.detail || t("فشل الحفظ", "Save failed")); }
    finally { setSaving(false); }
  };

  const cat = SERVICE_CATEGORIES.find((c) => c.v === form.category);
  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:border-yellow-500 outline-none w-full";

  // --- LIST VIEW ---
  if (editing === null) {
    return (
      <DashboardLayout title={t("صفحتي كمقدم خدمة", "My provider profile")}>
        <div className="space-y-6" data-testid="my-provider-page">
          <div className="glass-card rounded-3xl p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-yellow-500/10 blur-[120px] rounded-full" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <Badge className="bg-yellow-500/20 text-yellow-400 mb-2"><Briefcase size={12} className="ml-1" />{t("لوحة مقدم الخدمة", "Provider Dashboard")}</Badge>
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-1">{t("اعرض خدماتك للمجتمع العربي", "Showcase your services")}</h2>
                <p className="text-slate-400 text-sm max-w-xl">{t("سجّل خدماتك ليجدك آلاف العرب المقيمين في أمريكا.", "Register your services so thousands of Arabs across the US can find you.")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* "Manage Billing" opens the Stripe Customer Portal in a new tab so
                    the user can update payment methods, see invoices, and cancel
                    a subscription directly through Stripe. Only shown once any of
                    the user's listings has a real Stripe customer id on file. */}
                {mine.some((p) => p.stripe_customer_id) && (
                  <Button
                    onClick={openBillingPortal}
                    disabled={portalBusy}
                    variant="outline"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800"
                    data-testid="billing-portal-btn"
                  >
                    {portalBusy
                      ? <Loader2 size={16} className="ml-2 animate-spin" />
                      : <CreditCard size={16} className="ml-2" />}
                    {t("إدارة الفوترة", "Manage billing")}
                  </Button>
                )}
                <Button onClick={startNew} className="btn-primary" data-testid="new-provider-btn">
                  <Plus size={18} className="ml-2" />{t("ملف خدمة جديد", "New service profile")}
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 gap-4">{[1,2].map((i) => <div key={i} className="glass-card rounded-2xl h-40 skeleton" />)}</div>
          ) : mine.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <Briefcase className="text-slate-600 mx-auto mb-3" size={42} />
              <p className="text-slate-300 mb-1">{t("ليس لديك ملف بعد", "You don't have a profile yet")}</p>
              <p className="text-slate-500 text-sm mb-4">{t("سجّل ملفك الآن وانضم للدليل.", "Register and join the directory now.")}</p>
              <Button onClick={startNew} className="btn-primary"><Plus size={16} className="ml-2" />{t("ابدأ الآن", "Get started")}</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {mine.map((p) => (
                <div key={p.provider_id} className="glass-card rounded-2xl p-5 space-y-3" data-testid={`mine-${p.provider_id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate">{p.full_name}</h3>
                      {p.business_name && <p className="text-xs text-slate-400 truncate">{p.business_name}</p>}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">{subLabel(p.profession, language)}</Badge>
                        <StatusBadge status={p.status} t={t} />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">{p.city}, {p.state} · {p.zip_code}</p>

                  {/* ── Membership / subscription block ─────────────────────────────
                      Shows current status + expiry; offers free first month (one-time),
                      $4.99 second month (one-time), then $15/mo from the third month.
                      Listings only appear publicly while active. */}
                  <SubscriptionBlock
                    provider={p}
                    plans={plans}
                    busy={subBusy === p.provider_id}
                    onSubscribe={(plan) => subscribe(p.provider_id, plan)}
                    t={t}
                  />

                  <div className="flex gap-2 pt-2 border-t border-slate-800">
                    <Button size="sm" onClick={() => startEdit(p)} variant="outline" className="border-slate-700 text-white flex-1" data-testid={`edit-${p.provider_id}`}>
                      <Edit size={14} className="ml-1" />{t("تعديل", "Edit")}
                    </Button>
                    <Button size="sm" onClick={() => navigate(`/services/${p.provider_id}`)} variant="outline" className="border-slate-700 text-white">
                      <ExternalLink size={14} />
                    </Button>
                    <Button size="sm" onClick={() => remove(p.provider_id)} className="bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`del-${p.provider_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // --- EDIT / CREATE FORM ---
  return (
    <DashboardLayout title={editing === "new" ? t("ملف خدمة جديد", "New service profile") : t("تعديل الملف", "Edit profile")}>
      <div className="max-w-4xl space-y-6" data-testid="provider-form">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 text-yellow-500 hover:underline">
          <ArrowRight size={16} /> {t("رجوع", "Back")}
        </button>

        <div className="glass-card rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white text-lg border-b border-slate-800 pb-3">{t("المعلومات الأساسية", "Basic Info")}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm text-slate-400 block mb-1">{t("الاسم الكامل *", "Full name *")}</label>
              <Input className={inputCls} value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} data-testid="f-name" /></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("اسم العمل/الشركة", "Business name")}</label>
              <Input className={inputCls} value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} data-testid="f-biz" /></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("الفئة *", "Category *")}</label>
              <select className={inputCls} value={form.category} onChange={(e) => setForm({...form, category: e.target.value, profession: ""})} data-testid="f-cat">
                <option value="">--</option>
                {SERVICE_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{language === "ar" ? c.ar : c.en}</option>)}
              </select></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("التخصص *", "Profession *")}</label>
              <select className={inputCls} value={form.profession} onChange={(e) => setForm({...form, profession: e.target.value})} disabled={!cat} data-testid="f-prof">
                <option value="">--</option>
                {cat?.subs.map((s) => <option key={s} value={s}>{subLabel(s, language)}</option>)}
              </select></div>
          </div>
          <div><label className="text-sm text-slate-400 block mb-1">{t("الوصف *", "Description *")}</label>
            <Textarea className={inputCls} rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder={t("اشرح خدماتك، خبرتك، وميزاتك...", "Describe your services, experience, and advantages...")} data-testid="f-desc" /></div>
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white text-lg border-b border-slate-800 pb-3">{t("التواصل والموقع", "Contact & Location")}</h3>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t("ابحث في خرائط Google لملء الموقع", "Search Google to auto-fill location")}</label>
            <PlacesAutocompleteInput
              placeholder={t("ابحث عن العنوان أو الشركة...", "Search address or business...")}
              types="address"
              dataTestId="prov-place-search"
              onSelect={(d) => {
                if (d.state) setForm((f) => ({ ...f, state: d.state }));
                if (d.city) setForm((f) => ({ ...f, city: d.city }));
                if (d.zip_code) setForm((f) => ({ ...f, zip_code: d.zip_code }));
              }}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm text-slate-400 block mb-1">{t("الهاتف *", "Phone *")}</label>
              <Input className={inputCls} value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="f-phone" /></div>
            <div><label className="text-sm text-slate-400 block mb-1">WhatsApp</label>
              <Input className={inputCls} value={form.whatsapp} onChange={(e) => setForm({...form, whatsapp: e.target.value})} /></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("البريد", "Email")}</label>
              <Input className={inputCls} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("الموقع الإلكتروني", "Website")}</label>
              <Input className={inputCls} value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} /></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("الولاية *", "State *")}</label>
              <select className={inputCls} value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} data-testid="f-state">
                <option value="">--</option>
                {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
              </select></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("المدينة *", "City *")}</label>
              <Input className={inputCls} value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="f-city" /></div>
            <div><label className="text-sm text-slate-400 block mb-1">ZIP *</label>
              <Input className={inputCls} value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})} data-testid="f-zip" /></div>
            <div><label className="text-sm text-slate-400 block mb-1">{t("نطاق الخدمة", "Service area")}</label>
              <Input className={inputCls} value={form.service_area} onChange={(e) => setForm({...form, service_area: e.target.value})} placeholder={t("مثال: نيويورك ومحيطها 30 ميل", "e.g., NYC metro 30 mi")} /></div>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white text-lg border-b border-slate-800 pb-3">{t("الخبرة واللغات", "Experience & Languages")}</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="text-sm text-slate-400 block mb-1">{t("سنوات الخبرة", "Years experience")}</label>
              <Input type="number" className={inputCls} value={form.years_experience} onChange={(e) => setForm({...form, years_experience: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="text-sm text-slate-400 block mb-1">{t("ساعات العمل", "Business hours")}</label>
              <Input className={inputCls} value={form.business_hours} onChange={(e) => setForm({...form, business_hours: e.target.value})} placeholder={t("9 صباحاً - 6 مساءً (سبت-خميس)", "9am - 6pm (Mon-Sat)")} /></div>
          </div>
          <div><label className="text-sm text-slate-400 block mb-1">{t("اللغات (مفصولة بفواصل)", "Languages (comma separated)")}</label>
            <Input className={inputCls} value={form.languages.join(", ")} onChange={(e) => setForm({...form, languages: e.target.value.split(",").map((s) => s.trim())})} placeholder="Arabic, English" /></div>
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white text-lg border-b border-slate-800 pb-3">{t("الصور", "Photos")}</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <div><label className="text-sm text-slate-400 block mb-2">{t("الصورة الشخصية", "Profile photo")}</label>
              <ImageUploader value={form.profile_photo ? [form.profile_photo] : []} onChange={(p) => setForm({...form, profile_photo: p[0] || ""})} max={1} /></div>
            <div><label className="text-sm text-slate-400 block mb-2">{t("صورة الغلاف", "Cover photo")}</label>
              <ImageUploader value={form.cover_photo ? [form.cover_photo] : []} onChange={(p) => setForm({...form, cover_photo: p[0] || ""})} max={1} /></div>
          </div>
          <div><label className="text-sm text-slate-400 block mb-2">{t("معرض الأعمال (حتى 10)", "Portfolio (up to 10)")}</label>
            <ImageUploader
              value={form.portfolio_photos}
              onChange={(p) => setForm({...form, portfolio_photos: p})}
              listingType={editing !== "new" ? "provider" : undefined}
              listingId={editing !== "new" ? editing : undefined}
              mediaType="image"
            /></div>
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-5">
          {/* Direct video uploads — unlimited, admin-reviewed before going live */}
          <VideoUploader
            value={(form.portfolio_videos || []).filter((v) => !/^https?:\/\//i.test(v))}
            onChange={(vids) => {
              const youtube = (form.portfolio_videos || []).filter((v) => /^https?:\/\//i.test(v));
              setForm({ ...form, portfolio_videos: [...youtube, ...vids] });
            }}
            listingType={editing !== "new" ? "provider" : undefined}
            listingId={editing !== "new" ? editing : undefined}
          />
          <div className="border-t border-slate-800 pt-4 space-y-2">
            <h4 className="font-bold text-white text-sm">{t("روابط يوتيوب (اختياري)", "YouTube links (optional)")}</h4>
            <Input className={inputCls} placeholder="https://youtube.com/watch?v=..." value={(form.portfolio_videos || []).filter((v) => /^https?:\/\//i.test(v)).join(", ")}
              onChange={(e) => {
                const direct = (form.portfolio_videos || []).filter((v) => !/^https?:\/\//i.test(v));
                const youtube = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                setForm({...form, portfolio_videos: [...direct, ...youtube]});
              }} />
            <p className="text-xs text-slate-500">{t("افصل بين الروابط بفواصل", "Separate links with commas")}</p>
          </div>
        </div>

        <div className="flex gap-3 sticky bottom-4">
          <Button onClick={save} disabled={saving} className="btn-primary flex-1 h-12" data-testid="save-provider-btn">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} className="ml-2" />{editing === "new" ? t("نشر للمراجعة", "Submit for review") : t("حفظ التعديلات", "Save changes")}</>}
          </Button>
          <Button onClick={() => setEditing(null)} variant="outline" className="border-slate-700 text-white h-12">
            {t("إلغاء", "Cancel")}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
